import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Shield, Activity, AlertTriangle, FileCode, TestTube, Wrench,
  Loader2, RefreshCw, TrendingUp, TrendingDown, ChevronRight,
  Eye, Save, Copy, BarChart3, Clock, Gauge, Target,
  AlertCircle, CheckCircle, Code, FileText, Timer, Play,
  Power, Zap,
} from "lucide-react";

const SUB_TABS = [
  { key: "dashboard", label: "Dashboard", icon: Gauge },
  { key: "cronjobs", label: "AI Cron", icon: Timer },
  { key: "issues", label: "Issues", icon: AlertTriangle },
  { key: "refactor", label: "Refactor", icon: Wrench },
  { key: "tests", label: "Test Generator", icon: TestTube },
];

export default function DevOpsPanel() {
  const [subTab, setSubTab] = useState("dashboard");

  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="devops-panel">
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
              subTab === t.key ? "bg-[#1e293b] text-lime-400" : "text-slate-500 hover:text-slate-300"
            }`} data-testid={`devops-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {subTab === "dashboard" && <HealthDashboard />}
        {subTab === "cronjobs" && <CronJobsPanel />}
        {subTab === "issues" && <IssuesPanel />}
        {subTab === "refactor" && <RefactorPanel />}
        {subTab === "tests" && <TestGeneratorPanel />}
      </div>
    </div>
  );
}

// ==================== HEALTH DASHBOARD ====================
function HealthDashboard() {
  const [health, setHealth] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, tRes] = await Promise.all([
        api.get("/devops/health"),
        api.get("/devops/health/timeline?days=14"),
      ]);
      setHealth(hRes.data);
      setTimeline(tRes.data.timeline || []);
    } catch (err) { toast.error("Lỗi tải health data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const forceScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/devops/health/scan?scope=all");
      setHealth(data);
      toast.success("Scan hoàn tất");
      const tRes = await api.get("/devops/health/timeline?days=14");
      setTimeline(tRes.data.timeline || []);
    } catch { toast.error("Lỗi scan"); }
    finally { setScanning(false); }
  };

  const scoreColor = (s) => s >= 7 ? "text-lime-400" : s >= 5 ? "text-amber-400" : "text-red-400";
  const scoreBg = (s) => s >= 7 ? "bg-lime-500/15 border-lime-500/20" : s >= 5 ? "bg-amber-500/15 border-amber-500/20" : "bg-red-500/15 border-red-500/20";

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="health-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Shield className="w-5 h-5 text-lime-400" /> Technical Health</h2>
        <Button onClick={forceScan} disabled={scanning} variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-lime-400 gap-1.5 text-xs" data-testid="scan-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Đang scan..." : "Scan ngay"}
        </Button>
      </div>

      {health && (
        <>
          {/* Overall Score Gauge */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className={`flex flex-col items-center justify-center rounded-2xl p-6 border ${scoreBg(health.overall_score)} min-w-[160px]`}>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Overall Score</p>
              <p className={`text-5xl font-black ${scoreColor(health.overall_score)}`}>{health.overall_score}</p>
              <p className="text-xs text-slate-500 mt-1">/10</p>
            </div>

            {/* Score Breakdown (Radar-style cards) */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 flex-1">
              {[
                { key: "maintainability", label: "Bảo trì", icon: Wrench },
                { key: "complexity", label: "Độ phức tạp", icon: Activity },
                { key: "coupling", label: "Coupling", icon: Target },
                { key: "test_coverage", label: "Test", icon: TestTube },
                { key: "duplication", label: "Trùng lặp", icon: Code },
              ].map(m => {
                const val = health.scores?.[m.key] || 0;
                return (
                  <div key={m.key} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 flex flex-col items-center">
                    <m.icon className={`w-4 h-4 ${scoreColor(val)} mb-1`} />
                    <p className={`text-2xl font-bold ${scoreColor(val)}`}>{val}</p>
                    <p className="text-[9px] text-slate-500 mt-0.5">{m.label}</p>
                    {/* Bar */}
                    <div className="w-full h-1.5 bg-[#0f172a] rounded-full mt-2">
                      <div className={`h-full rounded-full ${val >= 7 ? "bg-lime-500" : val >= 5 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${val * 10}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { label: "Files", value: health.details?.total_files || 0 },
              { label: "Dòng code", value: (health.details?.total_lines || 0).toLocaleString("vi-VN") },
              { label: "Issues", value: health.details?.total_issues || 0, warn: true },
              { label: "Test files", value: health.details?.test_files || 0 },
              { label: "File lớn", value: health.details?.large_files_count || 0, warn: true },
            ].map((s, i) => (
              <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2">
                <p className="text-[9px] text-slate-500 uppercase">{s.label}</p>
                <p className={`text-lg font-bold ${s.warn && s.value > 0 ? "text-amber-400" : "text-slate-200"}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Alerts */}
          {health.alerts && health.alerts.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-400" /> Cảnh báo ({health.alerts.length})</p>
              {health.alerts.map((a, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  a.severity === "critical" ? "bg-red-500/10 border-red-500/20" : a.severity === "high" ? "bg-amber-500/10 border-amber-500/20" : "bg-slate-500/10 border-[#334155]"
                }`}>
                  <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${a.severity === "critical" ? "text-red-400" : "text-amber-400"}`} />
                  <span className="text-xs text-slate-300">{a.message}</span>
                  <Badge className={`text-[8px] border-0 ml-auto ${a.severity === "critical" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>{a.severity}</Badge>
                </div>
              ))}
            </div>
          )}

          {/* Timeline */}
          {timeline.length > 1 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-3 flex items-center gap-1"><Clock className="w-3 h-3" /> Tech Health Timeline</p>
              <div className="flex items-end gap-1 h-20">
                {timeline.map((t, i) => {
                  const h = Math.max(8, (t.overall_score / 10) * 100);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center group" title={`${t.scan_time?.split("T")[0]} — ${t.overall_score}/10`}>
                      <div className={`w-full rounded-t ${t.overall_score >= 7 ? "bg-lime-500" : t.overall_score >= 5 ? "bg-amber-500" : "bg-red-500"} transition-all group-hover:opacity-80`}
                        style={{ height: `${h}%` }} />
                      <p className="text-[7px] text-slate-600 mt-1 hidden md:block">{t.scan_time?.split("T")[0]?.slice(5)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== ISSUES PANEL ====================
function IssuesPanel() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/devops/analysis").then(r => { setAnalysis(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const SEV_C = { critical: "bg-red-500/20 text-red-400", high: "bg-amber-500/20 text-amber-400", medium: "bg-sky-500/20 text-sky-400", low: "bg-slate-500/20 text-slate-400" };

  const issues = (analysis?.issues || []).filter(i => !filter || i.severity === filter);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="issues-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" /> Code Issues</h2>
        <div className="flex gap-1">
          {["", "critical", "high", "medium", "low"].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-2 py-1 rounded text-[10px] ${filter === s ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>
              {s || "Tất cả"} {s && analysis ? `(${(analysis.issues || []).filter(i => i.severity === s).length})` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div> :
      <div className="space-y-1.5">
        {issues.length === 0 ? <p className="text-slate-500 text-center py-8">Không có issue nào.</p> :
        issues.map((issue, i) => (
          <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-lg px-4 py-2.5 flex items-start gap-3">
            <Badge className={`text-[8px] border-0 shrink-0 mt-0.5 ${SEV_C[issue.severity] || ""}`}>{issue.severity}</Badge>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-200">{issue.message}</p>
              <div className="flex gap-3 mt-1 text-[10px] text-slate-500">
                <span className="font-mono text-slate-400">{issue.file}</span>
                {issue.line && <span>Dòng {issue.line}</span>}
                {issue.function && <span>fn: {issue.function}</span>}
              </div>
            </div>
            <Badge className="bg-[#0f172a] text-slate-500 text-[8px] border-0">{issue.type?.replace("_", " ")}</Badge>
          </div>
        ))}
      </div>}

      {/* File stats summary */}
      {analysis?.summary?.large_files?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2 flex items-center gap-1"><FileCode className="w-3 h-3" /> File lớn cần tách</p>
          {analysis.summary.large_files.map((f, i) => (
            <div key={i} className="flex items-center justify-between py-1 border-b border-[#334155]/30 last:border-0">
              <span className="text-xs text-slate-300 font-mono">{f.file}</span>
              <span className="text-xs text-red-400 font-bold">{f.lines} dòng</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== REFACTOR PANEL ====================
function RefactorPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const endpoint = force ? "/devops/refactor/refresh" : "/devops/refactor";
      const method = force ? "post" : "get";
      const { data } = await api[method](endpoint);
      setData(data);
    } catch { toast.error("Lỗi tải suggestions"); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const RISK_C = { low: "bg-lime-500/20 text-lime-400", medium: "bg-amber-500/20 text-amber-400", high: "bg-red-500/20 text-red-400" };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="refactor-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Wrench className="w-5 h-5 text-lime-400" /> Refactor Advisor</h2>
        <Button onClick={() => fetch(true)} disabled={refreshing} variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-lime-400 gap-1.5 text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "AI đang phân tích..." : "Phân tích lại"}
        </Button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div> : data && (
        <>
          {/* Quick wins */}
          {data.quick_wins?.length > 0 && (
            <div className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-4">
              <p className="text-xs text-lime-400 font-medium mb-2 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Quick Wins</p>
              {data.quick_wins.map((w, i) => <p key={i} className="text-xs text-slate-300 mt-1">• {w}</p>)}
            </div>
          )}

          {/* Risk areas */}
          {data.risk_areas?.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <p className="text-xs text-red-400 font-medium mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Khu vực rủi ro</p>
              {data.risk_areas.map((r, i) => <p key={i} className="text-xs text-slate-300 mt-1">• {r}</p>)}
            </div>
          )}

          {/* Suggestions */}
          <div className="space-y-3">
            {(data.suggestions || []).map((s, i) => (
              <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-lime-400 font-mono font-bold">#{s.priority || i + 1}</span>
                      <h3 className="text-sm font-medium text-slate-200">{s.title}</h3>
                    </div>
                    <p className="text-xs text-slate-400">{s.description}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Badge className={`text-[8px] border-0 ${RISK_C[s.risk_level] || ""}`}>Risk: {s.risk_level}</Badge>
                    <Badge className={`text-[8px] border-0 ${s.impact === "high" ? "bg-red-500/20 text-red-400" : "bg-sky-500/20 text-sky-400"}`}>Impact: {s.impact}</Badge>
                  </div>
                </div>

                {/* Affected files */}
                {s.affected_files?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {s.affected_files.map((f, j) => <span key={j} className="text-[9px] font-mono text-slate-500 bg-[#0f172a] px-1.5 py-0.5 rounded">{f}</span>)}
                  </div>
                )}

                {/* Pre-refactor tests */}
                {s.pre_refactor_tests?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[9px] text-amber-400 uppercase">Test trước khi refactor:</p>
                    {s.pre_refactor_tests.map((t, j) => <p key={j} className="text-[10px] text-slate-400 mt-0.5">• {t}</p>)}
                  </div>
                )}

                {/* Steps */}
                {s.steps?.length > 0 && (
                  <details className="group">
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-300">Xem {s.steps.length} bước thực hiện</summary>
                    <ol className="mt-1.5 pl-4 space-y-0.5">
                      {s.steps.map((step, j) => <li key={j} className="text-[10px] text-slate-400 list-decimal">{step}</li>)}
                    </ol>
                  </details>
                )}

                {s.estimated_effort && <p className="text-[9px] text-slate-600 mt-2">Effort: {s.estimated_effort}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ==================== TEST GENERATOR ====================
function TestGeneratorPanel() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState("");
  const [generating, setGenerating] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/devops/testable-files").then(r => { setFiles(r.data.files || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const generate = async () => {
    if (!selectedFile) { toast.error("Chọn file cần generate test"); return; }
    setGenerating(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/devops/generate-tests?module_path=${encodeURIComponent(selectedFile)}`);
      setTestResult(data);
      if (data.error) toast.error(data.error);
      else toast.success(`Tạo ${data.test_count || 0} test cases`);
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setGenerating(false); }
  };

  const saveTest = async () => {
    if (!testResult?.test_code) return;
    setSaving(true);
    try {
      const filename = `test_${selectedFile.split("/").pop()?.replace(".py", "") || "generated"}`;
      const { data } = await api.post("/devops/save-test", { filename, code: testResult.test_code });
      toast.success(data.message);
    } catch { toast.error("Lỗi lưu"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="test-generator">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><TestTube className="w-5 h-5 text-lime-400" /> AI Test Generator</h2>

      <div className="flex gap-2">
        <select value={selectedFile} onChange={e => setSelectedFile(e.target.value)}
          className="flex-1 bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2 text-xs text-slate-200" data-testid="file-select">
          <option value="">Chọn file cần generate test...</option>
          {files.map(f => <option key={f.path} value={f.path}>{f.path} ({f.lines} dòng)</option>)}
        </select>
        <Button onClick={generate} disabled={generating || !selectedFile}
          className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1.5 text-xs" data-testid="generate-tests-btn">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TestTube className="w-3.5 h-3.5" />}
          {generating ? "Đang tạo..." : "Generate Tests"}
        </Button>
      </div>

      {testResult && (
        <div className="space-y-3">
          {testResult.error ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-xs text-red-400">{testResult.error}</div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <Badge className="bg-lime-500/20 text-lime-400 text-[10px] border-0">{testResult.test_count || 0} tests</Badge>
                <Badge className="bg-sky-500/20 text-sky-400 text-[10px] border-0">Coverage: {testResult.coverage_estimate || "N/A"}</Badge>
                <div className="ml-auto flex gap-1.5">
                  <Button onClick={saveTest} disabled={saving} size="sm"
                    className="bg-lime-500/10 text-lime-400 hover:bg-lime-500/20 border border-lime-500/20 h-7 text-[10px] gap-1" data-testid="save-test-btn">
                    <Save className="w-3 h-3" /> {saving ? "Lưu..." : "Lưu vào tests/"}
                  </Button>
                  <Button onClick={() => { navigator.clipboard.writeText(testResult.test_code); toast.success("Copied!"); }}
                    size="sm" className="bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 border border-[#334155] h-7 text-[10px] gap-1">
                    <Copy className="w-3 h-3" /> Copy
                  </Button>
                </div>
              </div>

              {testResult.test_description?.length > 0 && (
                <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-3">
                  <p className="text-[9px] text-slate-500 uppercase mb-1.5">Test Cases</p>
                  {testResult.test_description.map((d, i) => (
                    <p key={i} className="text-[10px] text-slate-400 mt-0.5">• {d}</p>
                  ))}
                </div>
              )}

              <pre className="bg-[#0f172a] border border-[#334155] rounded-lg p-4 text-[10px] text-slate-300 overflow-x-auto max-h-[50vh] overflow-y-auto font-mono leading-relaxed whitespace-pre-wrap">
                {testResult.test_code}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ==================== CRON JOBS PANEL ====================
function CronJobsPanel() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, h, c] = await Promise.all([
        api.get("/ai-cron/status"),
        api.get("/ai-cron/history?limit=10"),
        api.get("/ai-cron/cache-status"),
      ]);
      setStatus(s.data);
      setHistory(h.data.history || []);
      setCacheStatus(c.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll when running
  useEffect(() => {
    if (!status?.running) { setPolling(false); return; }
    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/ai-cron/status");
        setStatus(data);
        if (!data.running) {
          clearInterval(interval);
          setPolling(false);
          fetchAll();
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [status?.running, fetchAll]);

  const runAll = async () => {
    try {
      await api.post("/ai-cron/run-all");
      toast.success("Bắt đầu chạy toàn bộ AI CTO System");
      const { data } = await api.get("/ai-cron/status");
      setStatus(data);
    } catch { toast.error("Loi"); }
  };

  const runModule = async (key) => {
    try {
      await api.post(`/ai-cron/run-module/${key}`);
      toast.success("Đang chạy...");
      const { data } = await api.get("/ai-cron/status");
      setStatus(data);
    } catch { toast.error("Loi"); }
  };

  const toggleModule = async (key, enabled) => {
    const modules = { ...(status?.enabled_modules || {}), [key]: enabled };
    try {
      await api.put("/ai-cron/settings", modules);
      setStatus(prev => ({ ...prev, enabled_modules: modules }));
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;

  const MODULE_ICONS = { research: Target, tiktok: Eye, marketing: BarChart3, product_lab: Code, growth: TrendingUp, devops: Gauge, copilot_insight: Zap };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="cron-jobs-panel">
      {/* Header + Run All Button */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Timer className="w-5 h-5 text-lime-400" /> AI Cron System</h2>
          <p className="text-[10px] text-slate-500">Tự động chạy AI 0h-6h hàng ngày. Hoặc chạy thủ công.</p>
        </div>
        <Button onClick={runAll} disabled={status?.running} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1.5 text-xs font-semibold" data-testid="run-all-btn">
          {status?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {status?.running ? "Đang chạy..." : "Chạy toàn bộ AI"}
        </Button>
      </div>

      {/* Progress Bar (when running) */}
      {status?.running && (
        <div className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-4 space-y-2" data-testid="cron-progress">
          <div className="flex items-center justify-between">
            <span className="text-xs text-lime-400 font-medium">Đang chạy: {status.current_module}</span>
            <span className="text-xs text-slate-400">{status.progress_pct}%</span>
          </div>
          <div className="w-full h-2 bg-[#0f172a] rounded-full overflow-hidden">
            <div className="h-full bg-lime-500 rounded-full transition-all duration-500" style={{ width: `${status.progress_pct}%` }} />
          </div>
          <div className="flex gap-1 flex-wrap">
            {status.completed?.map(k => <Badge key={k} className="text-[8px] border-0 bg-lime-500/20 text-lime-400">{k}</Badge>)}
            {status.failed?.map(k => <Badge key={k} className="text-[8px] border-0 bg-red-500/20 text-red-400">{k}</Badge>)}
          </div>
        </div>
      )}

      {/* Module List */}
      <div className="space-y-1.5">
        <p className="text-xs text-slate-400 font-medium">Modules (bật/tắt + chạy riêng)</p>
        {status?.modules?.map(mod => {
          const Icon = MODULE_ICONS[mod.key] || Timer;
          const enabled = status.enabled_modules?.[mod.key] !== false;
          const cache = cacheStatus?.[mod.key];
          return (
            <div key={mod.key} className="flex items-center justify-between bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2" data-testid={`module-${mod.key}`}>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleModule(mod.key, !enabled)}
                  className={`w-8 h-4 rounded-full relative transition-colors ${enabled ? "bg-lime-500" : "bg-[#334155]"}`}>
                  <div className={`w-3 h-3 rounded-full bg-white absolute top-0.5 transition-all ${enabled ? "left-4.5 ml-0.5" : "left-0.5"}`} style={{ left: enabled ? '17px' : '2px' }} />
                </button>
                <Icon className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs text-slate-200">{mod.name}</span>
                <span className="text-[9px] text-slate-600">{String(mod.hour).padStart(2,"0")}:{String(mod.minute).padStart(2,"0")}</span>
              </div>
              <div className="flex items-center gap-2">
                {cache?.cached && (
                  <span className="text-[9px] text-lime-400 bg-lime-500/10 px-1.5 py-0.5 rounded">
                    {cache.updated_at ? new Date(cache.updated_at).toLocaleTimeString("vi-VN", {hour:"2-digit", minute:"2-digit"}) : "OK"}
                  </span>
                )}
                {!cache?.cached && <span className="text-[9px] text-slate-600">Chưa có</span>}
                <Button onClick={() => runModule(mod.key)} disabled={status?.running} size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-500 hover:text-lime-400">
                  <Play className="w-3 h-3" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cache Status Summary */}
      {cacheStatus && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-cyan-400" /> Trạng thái cache</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(cacheStatus).map(([key, val]) => (
              <div key={key} className={`rounded-lg p-2 text-center ${val.cached ? "bg-lime-500/5 border border-lime-500/20" : "bg-[#0f172a] border border-[#334155]"}`}>
                <p className="text-[9px] text-slate-500 uppercase">{key.replace("_", " ")}</p>
                <p className={`text-xs font-bold ${val.cached ? "text-lime-400" : "text-slate-600"}`}>
                  {val.cached ? (val.updated_at ? new Date(val.updated_at).toLocaleTimeString("vi-VN", {hour:"2-digit", minute:"2-digit"}) : "Co") : "—"}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Run History */}
      {history.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2">Lịch sử chạy</p>
          <div className="space-y-1.5">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
                <div className="flex items-center gap-2">
                  <Badge className={`text-[8px] border-0 ${h.trigger === "manual" ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-500/20 text-slate-400"}`}>
                    {h.trigger === "manual" ? "Thủ công" : "Tự động"}
                  </Badge>
                  <span className="text-[10px] text-slate-300">
                    {h.started_at ? new Date(h.started_at).toLocaleString("vi-VN", {day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit"}) : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-slate-500">{h.duration_seconds}s</span>
                  {h.results && (
                    <span className="text-[9px] text-lime-400">
                      {Object.values(h.results).filter(r => r.status === "success").length}/{Object.keys(h.results).length}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last Run Info */}
      {status?.last_run && (
        <p className="text-[10px] text-slate-600 text-center">
          Lần chạy cuối: {status.last_run.started_at ? new Date(status.last_run.started_at).toLocaleString("vi-VN") : "Chưa chạy"}
        </p>
      )}
    </div>
  );
}
